import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function IslesOfScillyCouncilPage() {
  const faqItems = [
    {
      question: 'The Isles of Scilly has a GREEN condition rating — can I still claim?',
      answer: 'Yes. The DfT Condition scorecard is GREEN because the council\'s single 2022 SCANNER survey found just 0.99% of carriageway length in RED condition. Your claim turns on the specific defect that damaged your vehicle, not borough-wide averages. The council itself says Amber and Red survey results "are largely those areas that were not resurfaced in 2014" — so location on the network matters. Section 58 depends on whether that defect was reasonably inspected and repaired in time.',
    },
    {
      question: 'I hit a pothole on Tresco, St Agnes, Bryher or St Martin\'s — who is responsible?',
      answer: 'Not the Council of the Isles of Scilly for adopted highways. The council\'s transparency report states its responsibilities "do not extend to the rest of the archipelago of inhabited off-islands (St Agnes, Bryher, Tresco and St Martin\'s)." It manages highway assets on St Mary\'s only. Check who maintains the road where your incident occurred before you claim.',
    },
    {
      question: 'Why is best practice RED when condition is GREEN?',
      answer: 'The DfT Best Practice scorecard is RED because of process gaps the council itself documents: only one condition survey (2022), no highway condition data before that date, majority reactive maintenance historically, no preventative works planned for 2025/26, and limited economies of scale on a 14.48km network. GREEN condition reflects a single snapshot; RED best practice reflects how the council measures, plans and maintains that tiny network over time.',
    },
    {
      question: 'The council filled only 38 potholes in 2024/25 — does a small number weaken my claim?',
      answer: 'No — scale is relative. On 14.48km of adopted roads, 38 pothole repairs in one year is still roughly one reactive fix every 10 days. The council forecasts around 40 refills for 2025/26. Over five years it recorded 247 pothole fills. A small absolute number on England\'s smallest highway network still shows defects forming between inspections — especially where the council admits work has been "mainly on pothole repairs" rather than planned maintenance.',
    },
    {
      question: 'Does the council spending more than its DfT allocation help its Section 58 defence?',
      answer: 'It can strengthen the council\'s position on aggregate funding — projected 2025/26 capital spend of £126,000 against a £53,000 DfT allocation earns a GREEN Spend scorecard. But Section 58 turns on the specific defect: prior reports, inspection records for your road, and whether reactive pothole filling addressed the hazard before your incident. The council\'s own report says it is "working towards" asset management principles but delivery is constrained by island scale and remoteness.',
    },
    {
      question: 'Should I report the pothole to the council before claiming?',
      answer: 'Yes. Reporting creates a dated record that the council knew about the defect — useful if it was reported before your incident or left unrepaired. The council publishes a dedicated pothole reporting page and can be contacted at highways@scilly.gov.uk for St Mary\'s highways. Keep reference numbers, confirmation emails and photos.',
    },
    {
      question: 'Does climate change and storm risk mentioned in the report affect my claim?',
      answer: 'It provides context, not a free pass for either side. The council states the islands face "significant" threat from sea-level rise and increased storm activity, which poses risks to highway sustainability. That is documented awareness of elevated environmental stress on St Mary\'s roads — relevant when asking whether inspection and repair frequency was reasonable for a network exposed to Atlantic weather, but it does not automatically establish or defeat liability.',
    },
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Isles of Scilly Pothole Claims | 14.48km Road Network | Fixtyer"
        description="England's smallest highway authority manages just 14.48km of roads on St Mary's. DfT rates condition and spend GREEN but best practice RED. See what the council's transparency data means for your pothole claim."
        canonical="/councils/isles-of-scilly"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Isles of Scilly', url: '/councils/isles-of-scilly' },
        ]}
      />
      <FAQSchema faqs={faqItems} />
      <ServiceSchema
        councilName="Council of the Isles of Scilly"
        councilSlug="isles-of-scilly"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/isles-of-scilly"
        headline="Isles of Scilly Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Isles of Scilly</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            England&apos;s Smallest Highway Authority: 14.48km on One Island
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            The Council of the Isles of Scilly maintains just{' '}
            <strong className="text-white">14.48km of adopted roads</strong> on St Mary&apos;s — within a
            16km&sup2; archipelago and with <strong className="text-white">zero B or C roads</strong>. The DfT
            awards <strong className="text-emerald-400">GREEN</strong> for condition and spend, yet the overall
            rating is <strong className="text-amber-400">AMBER</strong> because{' '}
            <strong className="text-rose-400">best practice is RED</strong>: one survey since 2022, reactive
            pothole repairs as the default, and no preventative programme planned for 2025/26.
          </p>

          <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-emerald-400">14.48km</div>
            <div className="text-white font-medium mt-2">Total adopted road network — St Mary&apos;s only</div>
            <div className="text-brand-300 text-sm mt-1">
              7.56km of A-roads and 6.92km of unclassified roads, plus 6.68km of footways. Off-islands
              (Tresco, St Agnes, Bryher, St Martin&apos;s) sit outside this figure entirely.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The network */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            A Network Unlike Any Other Authority
          </h2>
          <p className="text-surface-600 mb-8">
            The council&apos;s own transparency report describes a highway estate scaled to island life — not
            mainland county metrics
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Asset type</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Length (km)</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Notes</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">A-roads</td>
                  <td className="px-6 py-4 text-center font-mono">7.56</td>
                  <td className="px-6 py-4 text-surface-600">52% of carriageway network</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">B and C roads</td>
                  <td className="px-6 py-4 text-center font-mono font-bold">0</td>
                  <td className="px-6 py-4 text-surface-600">No classified B/C network at all</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">U-roads (unclassified)</td>
                  <td className="px-6 py-4 text-center font-mono">6.92</td>
                  <td className="px-6 py-4 text-surface-600">48% of carriageway network</td>
                </tr>
                <tr className="border-b border-surface-100 bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Total roads</td>
                  <td className="px-6 py-4 text-center font-mono font-bold text-brand-700">14.48</td>
                  <td className="px-6 py-4 text-surface-600">St Mary&apos;s adopted carriageways only</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">Footways</td>
                  <td className="px-6 py-4 text-center font-mono">6.68</td>
                  <td className="px-6 py-4 text-surface-600">Includes DVI where RAV could not access</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Cycleways</td>
                  <td className="px-6 py-4 text-center font-mono">0</td>
                  <td className="px-6 py-4 text-surface-600">LCWIP adopted 2023 for future walking/cycling routes</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">St Mary&apos;s only — not the whole archipelago</h3>
              <p className="text-surface-600 text-sm mb-3">
                The council manages highway assets on the island of St Mary&apos;s only. Its statutory
                responsibilities do not extend to inhabited off-islands — St Agnes, Bryher, Tresco and
                St Martin&apos;s.
              </p>
              <p className="text-surface-600 text-sm">
                If your incident was on a ferry-served off-island, confirm which authority maintains that
                road before submitting a claim to the Council of the Isles of Scilly.
              </p>
            </div>

            <div className="bg-brand-950 text-white rounded-xl p-6">
              <h3 className="font-semibold mb-3">What else the council maintains</h3>
              <p className="text-brand-200 text-sm">
                Beyond carriageways and footways, the network includes railings, gullies and manholes, traffic
                signs, junction markings, streetlights, bollards, slipways, verges, landscaped areas, drainage
                and road markings — all on a scale the council describes as reflective of the islands&apos;
                &ldquo;small scale and environment.&rdquo;
              </p>
            </div>
          </div>

          <QuoteCard
            quote="The Council of the Isles of Scilly, as Highway Authority, manages a small amount of public highway network, compared to mainland authorities which is reflective of the island's small scale and environment (total area of the Isles of Scilly: 16km2)."
            source="Council of the Isles of Scilly Local Highways Maintenance Transparency Report (30 June 2026)"
          />
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            One Survey, One Snapshot
          </h2>
          <p className="text-surface-600 mb-8">
            Condition data exists for 2022 only — the council commissioned its first SCANNER assessment that year
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-rose-700">Red</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-amber-700">Amber</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-emerald-700">Green</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic" colSpan={3}>No data collected</td>
                </tr>
                <tr className="border-b border-surface-100 bg-surface-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic" colSpan={3}>No data collected</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">0.99%</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">33.32%</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">65.69%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-surface-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic" colSpan={3}>No data collected</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic" colSpan={3}>No data collected</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200 text-center">
              <div className="text-3xl font-bold text-rose-600">0.99%</div>
              <div className="text-sm text-rose-800 mt-1">RED (2022)</div>
              <div className="text-xs text-surface-500 mt-1">~143 metres of carriageway</div>
            </div>
            <div className="bg-amber-50 rounded-xl p-6 border border-amber-200 text-center">
              <div className="text-3xl font-bold text-amber-600">33.32%</div>
              <div className="text-sm text-amber-800 mt-1">Amber (2022)</div>
              <div className="text-xs text-surface-500 mt-1">~4.8km may need treatment soon</div>
            </div>
            <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-200 text-center">
              <div className="text-3xl font-bold text-emerald-600">65.69%</div>
              <div className="text-sm text-emerald-800 mt-1">Green (2022)</div>
              <div className="text-xs text-surface-500 mt-1">Largely 2014 resurfacing investment</div>
            </div>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Using mainland assessment criteria would lead to an overspecification for the A roads and potentially highlight sections of the carriageway that are, in reality, fit for purpose. To ensure an objective, sensible assessment of the whole network the Condition Surveys assessed the survey data against the mainland C road thresholds, irrespective of the nominal classification assigned."
              source="Council of the Isles of Scilly Local Highways Maintenance Transparency Report (30 June 2026)"
            />
            <QuoteCard
              quote="The areas of highway (carriageway) which show Amber or Red results are largely those areas that were not resurfaced in 2014."
              source="Council of the Isles of Scilly Local Highways Maintenance Transparency Report (30 June 2026)"
            />
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold text-amber-900 mb-4">Why GREEN condition still leaves claim angles</h3>
            <p className="text-amber-800 text-sm mb-4">
              The DfT Condition scorecard is GREEN because RED share sits below England averages. But the
              council&apos;s own analysis shows <strong>over a third of the network (33.32%) in Amber</strong> —
              maintenance may be required soon — and the survey is now three years old with no follow-up
              published.
            </p>
            <p className="text-amber-800 text-sm">
              If your pothole sits on a section not resurfaced in 2014, the council has already mapped that
              corridor as higher risk in its own 2022 assessment. That is more useful to your claim than the
              headline GREEN badge.
            </p>
          </div>
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Spending Above DfT Allocation — On a Tiny Network
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of capital and revenue maintenance spend from the council&apos;s transparency report
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">DfT capital allocation</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Capital spend</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Revenue spend</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">£89,000</td>
                  <td className="px-6 py-4 text-center">£0</td>
                  <td className="px-6 py-4 text-center">£105,000</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">£0</td>
                  <td className="px-6 py-4 text-center">£0</td>
                  <td className="px-6 py-4 text-center">£115,000</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">£0</td>
                  <td className="px-6 py-4 text-center">£16,000</td>
                  <td className="px-6 py-4 text-center">£115,000</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">£0</td>
                  <td className="px-6 py-4 text-center">£18,000</td>
                  <td className="px-6 py-4 text-center">£142,000</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">£0</td>
                  <td className="px-6 py-4 text-center">£21,000</td>
                  <td className="px-6 py-4 text-center">£119,000</td>
                </tr>
                <tr className="bg-emerald-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2025/26 (projected)</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-700">£53,000</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-700">£126,000</td>
                  <td className="px-6 py-4 text-center font-bold">£92,000</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">More Than Double The DfT Cheque — Per Kilometre</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£53k</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£126k</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£8.7k</div>
                <div className="text-brand-300 text-sm">Projected capital spend per km of road</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Projected 2025/26 capital spend of <span className="text-emerald-400">£126,000</span> against a{' '}
              <span className="text-emerald-400">£53,000</span> DfT allocation earns the GREEN Spend scorecard.
              Island delivery costs are higher — the council cites limited economies of scale and specialist
              plant access — but aggregate spend does not tell you whether your specific defect was inspected
              or repaired in time.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            247 Pothole Repairs in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive filling on a network where the council admits most work has been pothole-led, not planned
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Per km of network</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">45</td>
                  <td className="px-6 py-4 text-center text-surface-500">3.1 per km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">50</td>
                  <td className="px-6 py-4 text-center text-surface-500">3.5 per km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">45</td>
                  <td className="px-6 py-4 text-center text-surface-500">3.1 per km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">69</td>
                  <td className="px-6 py-4 text-center text-amber-600">4.8 per km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">38</td>
                  <td className="px-6 py-4 text-center text-surface-500">2.6 per km</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">247</td>
                  <td className="px-6 py-4 text-center font-bold text-surface-600">17.1 per km</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="To date the majority of maintenance work to the highway carried out by the Council has been reactive – mainly on pothole repairs."
              source="Council of the Isles of Scilly Local Highways Maintenance Transparency Report (30 June 2026)"
            />
            <QuoteCard
              quote="Following a trend from previous years, there is likely to be in the region of 40 potholes that require re-filling as part of our reactive works."
              source="Council of the Isles of Scilly Local Highways Maintenance Transparency Report — 2025/26 plans (30 June 2026)"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">2023/24 peak: 69 fills</h3>
              <p className="text-surface-600 text-sm">
                The highest year in the five-year table — nearly five pothole repairs per kilometre of adopted
                road. On a mainland county that figure would be invisible; on 14.48km it means defects are
                still forming and being patched reactively, not eliminated through planned resurfacing.
              </p>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">No preventative programme for 2025/26</h3>
              <p className="text-rose-800 text-sm">
                The council states plainly: &ldquo;There is no preventative works planned at present for 25/26
                as the priority work required falls into reactive.&rdquo; Amber and Red sections from the 2022
                survey are targeted through reactive resurfacing if kit becomes available — not a rolling
                preventative programme.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Best practice RED */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Why Best Practice Is RED
          </h2>
          <p className="text-surface-600 mb-8">
            The council&apos;s own report documents the asset-management gaps behind the DfT&apos;s weakest scorecard
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">Survey and data gaps</h3>
              <ul className="text-rose-800 text-sm space-y-2">
                <li>• Only one SCANNER/RAV condition survey — commissioned in 2022</li>
                <li>• No highway condition data at all before 2022</li>
                <li>• No published condition data for 2023 or 2024</li>
                <li>• PAS2161-accredited surveying required from 2026/27 — not yet in place</li>
              </ul>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">Maintenance approach gaps</h3>
              <ul className="text-rose-800 text-sm space-y-2">
                <li>• Majority of historical work reactive — mainly pothole repairs</li>
                <li>• No preventative works planned for 2025/26</li>
                <li>• HMM and Asset Management Policy adopted but full lifecycle planning still developing</li>
                <li>• Three-to-five-year rolling programme promised only beyond 2025/26</li>
              </ul>
            </div>
          </div>

          <QuoteCard
            quote="The Council's ability to achieve full benefit from asset management principles is reduced because of limited economies of scale, challenges in accessing specialist plant and equipment and the additional resources and skills that may be required. Cost implications for delivering on the islands is likely to be higher due to these issues."
            source="Council of the Isles of Scilly Local Highways Maintenance Transparency Report (30 June 2026)"
          />

          <div className="bg-brand-950 text-white rounded-2xl p-8 mt-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for knowing
              the condition of its roads and acting on defects. For the Isles of Scilly, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last condition-surveyed — and was it in the 2022 exercise or never?</li>
              <li>• If Amber/Red sections map to areas not resurfaced in 2014, was your defect on one of those corridors?</li>
              <li>• Did you or anyone else report the pothole before your incident — and what was the response time?</li>
              <li>• With no preventative programme for 2025/26, how does the council demonstrate proactive knowledge of deterioration?</li>
            </ul>
            <p className="text-white font-medium">
              A GREEN condition badge built on one 2022 survey does not prove the council knew about your
              specific defect in 2025 or 2026 — especially where best practice is RED.
            </p>
          </div>
        </div>
      </section>

      {/* Climate / island context */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Atlantic Exposure and the 2014 Baseline
          </h2>
          <p className="text-surface-600 mb-8">
            Island context the council itself raises — storms, sea-level rise and a decade-old resurfacing programme
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Climate resilience</h3>
              <p className="text-surface-600 text-sm mb-3">
                The council states the islands are &ldquo;significantly at threat from Climate Change, specifically
                sea-level rise and increased storm activity,&rdquo; which poses the greatest risk to highway
                sustainability. Its Resilient Island Strategy calls for adaptation pathways for infrastructure
                including highways.
              </p>
              <p className="text-surface-600 text-sm">
                Atlantic weather accelerates surface deterioration on exposed St Mary&apos;s routes — relevant
                when assessing whether inspection frequency matched the environmental risk on your road.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The 2014 resurfacing cliff</h3>
              <p className="text-amber-800 text-sm mb-3">
                The council says good overall condition &ldquo;largely reflects the investment made by the Council
                in 2014 when the roads were resurfaced.&rdquo; Amber and Red results from the 2022 survey
                concentrate on sections outside that programme.
              </p>
              <p className="text-amber-800 text-sm">
                Roads not treated in 2014 are now eleven years into a cycle without a full preventative
                programme — pothole patching is the stated priority for 2025/26.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-surface-200">
            <h3 className="font-semibold text-brand-900 mb-3">2025/26 planned works</h3>
            <p className="text-surface-600 text-sm mb-3">
              The council is seeking opportunities to resurface larger Amber and Red areas if kit and equipment
              become available on the islands — framed as reactive works, not preventative. Some footway sections
              also require resurfacing from condition assessments and local inspection.
            </p>
            <p className="text-surface-600 text-sm">
              Beyond 2025/26, the council promises a prioritised rolling three-to-five-year programme derived
              from annual service, specialist surveys and inspections — but that is not yet operational.
            </p>
          </div>
        </div>
      </section>

      {/* Claims analysis */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a GREEN Condition, RED Best-Practice Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: small network, decent snapshot data — but process weaknesses open specific angles
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN condition — 0.99% RED at 2022 SCANNER survey
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend — projected capital more than double DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Highway Maintenance Manual and Asset Management Policy published
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> SCANNER-compliant RAV survey plus walked DVI for footways
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> LCWIP adopted 2023 — long-term active travel planning underway
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect the council to cite GREEN scorecards and its 2022 survey on roads resurfaced in 2014.
                Generic claims on well-maintained sections will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED best practice — single survey, no 2023/24 data, reactive default
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 33.32% of network in Amber — maintenance may be required soon
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Amber/Red sections mapped to roads not resurfaced in 2014
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 247 pothole repairs in five years — reactive patching, not prevention
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> No preventative works planned for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Off-island incidents fall outside council highway responsibility
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN condition on a tiny network, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect and location</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Confirm the incident was on St Mary&apos;s — not an off-island road outside council responsibility</li>
              <li>• Prior reports of the same pothole (council form, FixMyStreet) — proof of actual notice</li>
              <li>• Photos showing defect size, depth and visible age (weathered edges, previous patching)</li>
              <li>• Whether the road section was in the council&apos;s 2022 Amber/Red category — especially if not resurfaced in 2014</li>
              <li>• Atlantic exposure and storm history on that specific route</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites the council&apos;s own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-surface-50 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-4">
            Report the pothole on St Mary&apos;s first
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting creates a dated record that the council knew about the defect — useful if it was reported
            before your incident or left unrepaired. The council&apos;s highways team covers St Mary&apos;s only;
            contact highways@scilly.gov.uk for further information.
          </p>
          <a
            href="https://www.scilly.gov.uk/potholes"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-4 bg-brand-900 text-white font-semibold rounded-xl hover:bg-brand-800 transition-colors"
          >
            Report a pothole to the Council of the Isles of Scilly
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-sm mt-4">
            Use the online form with location, photos and contact details. Keep your reference number and any confirmation emails.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole on St Mary&apos;s?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            England&apos;s smallest network still demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No 2014 resurfacing gap cited</li>
                <li>• No RED best-practice process gaps argued</li>
                <li>• No prior-report search</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 14.48km network scope and St Mary&apos;s-only jurisdiction documented</li>
                <li>✅ 2022 Amber/Red sections and 2014 resurfacing gap cited</li>
                <li>✅ RED best-practice and reactive-maintenance record referenced</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Isles of Scilly data</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=isles-of-scilly"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Isles of Scilly Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {faqItems.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.scilly.gov.uk/environment-transport/highways"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Council of the Isles of Scilly Local Highways Maintenance Transparency Report (30 June 2026)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
